(() => {
  "use strict";

  const runtime = window.AssistCoClubPageRuntime || {};
  const params = new URLSearchParams(window.location.search);
  const pageHandle = params.get("club_page") || params.get("club");
  const itemId = params.get("club_item");
  const apiBaseUrl = normalizeBaseUrl(runtime.apiBaseUrl);

  if (!pageHandle || !itemId || !apiBaseUrl) {
    return;
  }

  let lastItemSignature = "";
  let applyTimer = 0;

  void init();

  async function init() {
    try {
      const context = await fetchClubItemContext(apiBaseUrl, pageHandle, itemId);
      if (!context?.item) {
        return;
      }

      const signature = JSON.stringify(context.item);
      lastItemSignature = signature;
      applyContext(context.item);
      observeDom(context.item);
    } catch (error) {
      console.error("[AssistCoClubPagePdp]", error);
    }
  }

  async function fetchClubItemContext(baseUrl, handle, clubItemId) {
    const url = new URL("/api/club-pages/public", baseUrl);
    url.searchParams.set("handle", handle);
    url.searchParams.set("itemId", clubItemId);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "omit",
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.error || "Kunne ikke hente klubbside-kontekst.");
    }

    return payload;
  }

  function observeDom(item) {
    const observer = new MutationObserver(() => {
      scheduleApply(item);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (target.closest("variant-selects, variant-radios, product-form, [data-trykk-root]")) {
        scheduleApply(item);
      }
    });

    document.addEventListener("input", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (target.closest("[data-trykk-root]")) {
        scheduleApply(item);
      }
    });

    window.addEventListener("popstate", () => scheduleApply(item));
    window.addEventListener("variant:change", () => scheduleApply(item));
  }

  function scheduleApply(item) {
    if (window.clearTimeout) {
      window.clearTimeout(applyTimer);
    }

    applyTimer = window.setTimeout(() => {
      if (lastItemSignature !== JSON.stringify(item)) {
        return;
      }
      applyContext(item);
    }, 80);
  }

  function applyContext(item) {
    const presentation = resolvePresentation(item);
    lockColorSelectors(item);
    overrideProductImages(presentation.imageUrl);
    ensureClubTrykkBoxes(item);
    filterTrykkBoxes(item);
    applyTrykkBoxOverrides(item);
    renderTrykkOverlays(item, presentation.useSecondary);
  }

  function ensureClubTrykkBoxes(item) {
    if (!Array.isArray(item.trykkOptions) || item.trykkOptions.length === 0) {
      cleanupFallbackTrykkBoxes(new Set());
      return;
    }

    const root = document.querySelector("[data-trykk-root]");
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const mount = root.querySelector("[data-trykk-mount]") || root;
    if (!(mount instanceof HTMLElement)) {
      return;
    }

    const sourceConfig = readTrykkRootConfig(root);
    const activeCodes = new Set();

    item.trykkOptions.forEach((option) => {
      const code = normalizeToken(option && option.code ? option.code : "");
      if (!code) {
        return;
      }

      activeCodes.add(code);
      const boxes = findTrykkBoxes(code);
      const hasNativeBox = boxes.some((box) => box.dataset.clubPageFallback !== "true");

      if (hasNativeBox) {
        boxes
          .filter((box) => box.dataset.clubPageFallback === "true")
          .forEach((box) => removeFallbackTrykkBox(box));
        return;
      }

      const fallbackBox = boxes.find((box) => box.dataset.clubPageFallback === "true");
      if (fallbackBox instanceof HTMLElement) {
        updateFallbackTrykkBox(fallbackBox, option, sourceConfig);
        return;
      }

      const createdBox = createFallbackTrykkBox(option, sourceConfig);
      if (!(createdBox instanceof HTMLElement)) {
        return;
      }

      const packTitle = resolveTrykkPackTitle(option, sourceConfig);
      const pack = findOrCreateFallbackPack(mount, packTitle, code);
      pack.appendChild(createdBox);
      bindFallbackTrykkBox(createdBox, option, sourceConfig);
    });

    cleanupFallbackTrykkBoxes(activeCodes);
    cleanupEmptyFallbackPacks();
  }

  function lockColorSelectors(item) {
    const tokens = buildAllowedColorTokens(item);
    if (tokens.size === 0) {
      return;
    }

    const radioFields = Array.from(document.querySelectorAll("fieldset")).filter((fieldset) =>
      isColorField(fieldset),
    );

    radioFields.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let firstMatch = null;
      let hasCheckedMatch = false;

      radios.forEach((radio) => {
        const value = String(
          radio.getAttribute("value") ||
            radio.getAttribute("data-option-value") ||
            radio.dataset.optionValue ||
            "",
        );
        const keep = matchesColorValue(value, tokens);
        const label =
          (radio.id && fieldset.querySelector(`label[for="${cssEscape(radio.id)}"]`)) ||
          radio.closest("label");

        radio.disabled = !keep;
        radio.hidden = !keep;

        if (label instanceof HTMLElement) {
          label.style.display = keep ? "" : "none";
        }

        const wrapper = radio.closest(".product-form__input, .swatch-input, .variant-input, .color-swatch, .form__label");
        if (wrapper instanceof HTMLElement && wrapper !== fieldset) {
          wrapper.style.display = keep ? "" : "none";
        }

        if (keep && radio.checked) {
          hasCheckedMatch = true;
        }

        if (keep && !firstMatch) {
          firstMatch = radio;
        }
      });

      if (firstMatch && !hasCheckedMatch) {
        firstMatch.checked = true;
        firstMatch.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    const selects = Array.from(document.querySelectorAll("select")).filter((select) =>
      isColorSelect(select),
    );

    selects.forEach((select) => {
      let firstMatchValue = "";
      let currentValueAllowed = false;

      Array.from(select.options).forEach((option) => {
        const keep = matchesColorValue(option.value || option.textContent || "", tokens);
        option.disabled = !keep;
        option.hidden = !keep;

        if (keep && option.value === select.value) {
          currentValueAllowed = true;
        }

        if (keep && !firstMatchValue) {
          firstMatchValue = option.value;
        }
      });

      if (firstMatchValue && !currentValueAllowed) {
        select.value = firstMatchValue;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  function overrideProductImages(imageUrl) {
    if (!imageUrl) {
      return;
    }

    const selectors = [
      "media-gallery img",
      ".product__media img",
      ".product__media-wrapper img",
      ".product-media-container img",
      ".thumbnail img",
      ".slider-mobile-gutter img",
    ];

    const images = document.querySelectorAll(selectors.join(", "));
    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) {
        return;
      }

      image.src = imageUrl;
      image.srcset = imageUrl;
      image.removeAttribute("loading");
      image.style.objectFit = "contain";
    });
  }

  function filterTrykkBoxes(item) {
    const root = document.querySelector("[data-trykk-root]");
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const codes = new Set((item.trykkCodes || []).map((code) => normalizeToken(code)));

    if (codes.size === 0) {
      root.style.display = "none";
      return;
    }

    root.style.display = "";

    const boxes = Array.from(root.querySelectorAll("[data-trykk-box]"));
    boxes.forEach((box) => {
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const code = normalizeToken(box.dataset.trykkCode || "");
      const visible = codes.has(code);
      box.hidden = !visible;
      box.style.display = visible ? "" : "none";
    });

    const packs = Array.from(root.querySelectorAll("[data-trykk-pack], .trykk-pack"));
    packs.forEach((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return;
      }

      const hasVisibleBox = Array.from(pack.querySelectorAll("[data-trykk-box]")).some((box) => {
        if (!(box instanceof HTMLElement)) {
          return false;
        }

        return !box.hidden && box.style.display !== "none";
      });

      pack.hidden = !hasVisibleBox;
      pack.style.display = hasVisibleBox ? "" : "none";
    });
  }

  function applyTrykkBoxOverrides(item) {
    if (!Array.isArray(item.trykkOptions)) {
      return;
    }

    item.trykkOptions.forEach((option) => {
      const box = findTrykkBox(option.code);
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const enabled = box.querySelector(".js-trykk-enabled");
      if (!(enabled instanceof HTMLInputElement)) {
        return;
      }

      const defaultOn = option.defaultSelected === true;
      const required = option.required === true;
      const locked = option.locked === true;
      const mustBeEnabled = required || locked;
      const desiredChecked = mustBeEnabled || defaultOn;
      const alreadyInitialized = box.dataset.clubPageInitialized === "true";

      box.dataset.required = String(required);
      box.dataset.defaultOn = String(desiredChecked);

      let nextChecked = enabled.checked;
      if (!alreadyInitialized) {
        nextChecked = desiredChecked;
        box.dataset.clubPageInitialized = "true";
      }
      if (mustBeEnabled) {
        nextChecked = true;
      }

      const didChange = enabled.checked !== nextChecked;
      enabled.checked = nextChecked;
      enabled.disabled = mustBeEnabled;

      const toggle = enabled.closest(".trykk-toggle");
      if (toggle instanceof HTMLElement) {
        toggle.classList.toggle("is-locked", mustBeEnabled);
      }

      syncFallbackTrykkBox(box, option);

      if (didChange) {
        enabled.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  function renderTrykkOverlays(item, useSecondary) {
    const overlays = collectActiveTrykkOverlays(item, useSecondary);

    const wrappers = collectMediaWrappers();
    wrappers.forEach((wrapper) => {
      const existing = wrapper.querySelector(".assistco-club-page-pdp-overlays");
      if (existing instanceof HTMLElement) {
        existing.remove();
      }

      if (overlays.length === 0) {
        return;
      }

      if (window.getComputedStyle(wrapper).position === "static") {
        wrapper.style.position = "relative";
      }

      const layer = document.createElement("div");
      layer.className = "assistco-club-page-pdp-overlays";
      Object.assign(layer.style, {
        position: "absolute",
        inset: "0",
        zIndex: "4",
        pointerEvents: "none",
        overflow: "hidden",
      });

      overlays.forEach((entry) => {
        const node = buildOverlayNode(entry.option, entry.overlay, entry.contentText);
        if (node) {
          layer.appendChild(node);
        }
      });

      if (layer.childElementCount > 0) {
        wrapper.appendChild(layer);
      }
    });
  }

  function collectMediaWrappers() {
    const selectors = [
      "media-gallery .product__media-item",
      "media-gallery .product__media",
      ".product__media-wrapper",
      ".product-media-container",
      ".product__media-item",
    ];

    const seen = new Set();
    return Array.from(document.querySelectorAll(selectors.join(", "))).filter((element) => {
      if (!(element instanceof HTMLElement)) {
        return false;
      }

      if (element.closest(".thumbnail, .slider-counter, .product__media-thumbnail")) {
        return false;
      }

      if (!element.querySelector("img")) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      if (rect.width < 180 || rect.height < 180) {
        return false;
      }

      if (seen.has(element)) {
        return false;
      }

      seen.add(element);
      return true;
    });
  }

  function buildOverlayNode(option, overlay, contentText) {
    if (!overlay || overlay.enabled !== true) {
      return null;
    }

    const node = document.createElement("div");
    node.className = `assistco-club-page__product-overlay assistco-club-page__product-overlay--${overlay.kind || "text"}`;
    Object.assign(node.style, {
      position: "absolute",
      left: `calc(50% + ${Number(overlay.offsetX || 0)}%)`,
      top: `calc(50% + ${Number(overlay.offsetY || 0)}%)`,
      transform: `translate(-50%, -50%) scale(${Number(overlay.scale || 100) / 100})`,
      transformOrigin: "center center",
      opacity: `${Number(overlay.opacity || 100) / 100}`,
      color: overlay.textColor || "#111827",
      fontSize: `${Number(overlay.fontSize || 28)}px`,
      fontFamily: overlay.fontFamily || "Arial, sans-serif",
      fontWeight: overlay.fontWeight || "700",
      fontStyle: overlay.fontStyle || "normal",
      textDecoration: overlay.textDecoration || "none",
      textAlign: overlay.textAlign || "center",
      textTransform: overlay.textTransform || "uppercase",
      letterSpacing: `${Number(overlay.letterSpacing || 0)}px`,
      whiteSpace: "pre-wrap",
      lineHeight: "1.1",
      maxWidth: "82%",
      pointerEvents: "none",
      textShadow: "0 2px 8px rgba(255, 255, 255, 0.65)",
    });

    if (overlay.kind === "image" && overlay.imageUrl) {
      const image = document.createElement("img");
      image.alt = option.title || "Trykk";
      image.src = overlay.imageUrl;
      Object.assign(image.style, {
        display: "block",
        maxWidth: "180px",
        maxHeight: "180px",
        objectFit: "contain",
      });
      node.appendChild(image);
      return node;
    }

    const text = document.createElement("span");
    text.textContent = contentText || overlay.text || option.title || "TRYKK";
    text.style.display = "block";
    node.appendChild(text);
    return node;
  }

  function collectActiveTrykkOverlays(item, useSecondary) {
    if (!Array.isArray(item.trykkOptions)) {
      return [];
    }

    return item.trykkOptions
      .map((option) => {
        const box = findTrykkBox(option.code);
        const overlay = resolveOverlayForPresentation(option, useSecondary);
        if (!overlay || overlay.enabled !== true) {
          return null;
        }

        if (!(box instanceof HTMLElement)) {
          if (
            option.defaultSelected === true ||
            option.required === true ||
            option.locked === true
          ) {
            return {
              option,
              overlay,
              contentText: overlay.text || option.title || "",
            };
          }

          return null;
        }

        const enabled = box.querySelector(".js-trykk-enabled");
        if (!(enabled instanceof HTMLInputElement) || enabled.checked !== true) {
          return null;
        }

        const contentText = resolveOverlayContentText(box, option, overlay);
        if (overlay.kind === "text" && !contentText && option.inputType !== "none") {
          return null;
        }

        return {
          option,
          overlay,
          contentText,
        };
      })
      .filter(Boolean);
  }

  function resolveOverlayForPresentation(option, useSecondary) {
    if (!useSecondary) {
      return option && option.overlay ? option.overlay : null;
    }

    if (option && option.usePrimaryOverlayOnSecondary !== false) {
      return option.overlay || null;
    }

    return option.secondaryOverlay || option.overlay || null;
  }

  function resolveOverlayContentText(box, option, overlay) {
    if (!overlay || overlay.kind !== "text") {
      return "";
    }

    const inputType = normalizeToken(option && option.inputType ? option.inputType : "none");
    if (!inputType || inputType === "none") {
      return overlay.text || option.title || "";
    }

    if (inputType === "text" || inputType === "number_range") {
      const value = Array.from(box.querySelectorAll(".js-trykk-unit"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);
      return value || "";
    }

    if (inputType === "name_number") {
      const nameValue = Array.from(box.querySelectorAll(".js-trykk-name"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);
      const numberValue = Array.from(box.querySelectorAll(".js-trykk-number"))
        .map((input) => String(input.value || "").trim())
        .find(Boolean);

      if (nameValue && numberValue) {
        return `${nameValue}\n${numberValue}`;
      }

      return nameValue || numberValue || "";
    }

    return overlay.text || option.title || "";
  }

  function findTrykkBox(code) {
    const normalizedCode = normalizeToken(code);
    if (!normalizedCode) {
      return null;
    }

    return Array.from(document.querySelectorAll("[data-trykk-box]")).find((box) => {
      if (!(box instanceof HTMLElement)) {
        return false;
      }

      if (box.hidden || box.style.display === "none") {
        return false;
      }

      return normalizeToken(box.dataset.trykkCode || "") === normalizedCode;
    });
  }

  function findTrykkBoxes(code) {
    const normalizedCode = normalizeToken(code);
    if (!normalizedCode) {
      return [];
    }

    return Array.from(document.querySelectorAll("[data-trykk-box]")).filter((box) => {
      if (!(box instanceof HTMLElement)) {
        return false;
      }

      return normalizeToken(box.dataset.trykkCode || "") === normalizedCode;
    });
  }

  function readTrykkRootConfig(root) {
    const configNode = root.querySelector("script[data-trykk-config]");
    if (!(configNode instanceof HTMLScriptElement)) {
      return null;
    }

    try {
      return JSON.parse(configNode.textContent || "{}");
    } catch (_error) {
      return null;
    }
  }

  function findSourceTrykkOption(option, sourceConfig) {
    if (!Array.isArray(sourceConfig && sourceConfig.options)) {
      return null;
    }

    const optionCode = normalizeToken(option && option.code ? option.code : "");
    const optionTitle = normalizeToken(option && option.title ? option.title : "");

    return (
      sourceConfig.options.find((entry) => normalizeToken(entry && entry.kode ? entry.kode : "") === optionCode) ||
      sourceConfig.options.find((entry) =>
        normalizeToken(entry && entry.tittel ? entry.tittel : "") === optionTitle,
      ) ||
      null
    );
  }

  function resolveTrykkPackTitle(option, sourceConfig) {
    const sourceOption = findSourceTrykkOption(option, sourceConfig);
    const packTitle =
      (option && option.packTitle) ||
      (sourceOption &&
        sourceOption.trykk_pack &&
        typeof sourceOption.trykk_pack.tittel === "string" &&
        sourceOption.trykk_pack.tittel) ||
      "Trykk";

    return String(packTitle || "Trykk").trim() || "Trykk";
  }

  function resolveFallbackTrykkDetails(option, sourceConfig) {
    const sourceOption = findSourceTrykkOption(option, sourceConfig);
    const trykkProduct = sourceOption && sourceOption.trykkprodukt ? sourceOption.trykkprodukt : {};

    return {
      code: String(option && option.code ? option.code : sourceOption && sourceOption.kode ? sourceOption.kode : "")
        .trim(),
      title: String(
        (option && option.title) || (sourceOption && sourceOption.tittel) || "Trykk",
      ).trim(),
      inputType: String(
        (option && option.inputType) || (sourceOption && sourceOption.inputtype) || "none",
      )
        .trim()
        .toLowerCase(),
      required: option && option.required === true,
      defaultSelected: option && option.defaultSelected === true,
      locked: option && option.locked === true,
      maxLength:
        option && typeof option.maxLength === "number"
          ? Number(option.maxLength)
          : toOptionalNumber(sourceOption && sourceOption.makslengdetekst),
      rangeMin:
        option && typeof option.rangeMin === "number"
          ? Number(option.rangeMin)
          : toOptionalNumber(sourceOption && sourceOption.minverdi),
      rangeMax:
        option && typeof option.rangeMax === "number"
          ? Number(option.rangeMax)
          : toOptionalNumber(sourceOption && sourceOption.maxverdi),
      defaultNumberValue:
        option && typeof option.defaultNumberValue === "number"
          ? Number(option.defaultNumberValue)
          : toOptionalNumber(sourceOption && sourceOption.defaultnumbervalue),
      numberUiMode: String(
        (option && option.numberUiMode) || (sourceOption && sourceOption.numberuimode) || "input",
      )
        .trim()
        .toLowerCase(),
      variantId: String(
        trykkProduct.variant_id ||
          trykkProduct.variantId ||
          trykkProduct.id ||
          "",
      ).trim(),
      variantSku: String(trykkProduct.variant_sku || trykkProduct.sku || "").trim(),
      priceText: String(trykkProduct.price_text || "").trim(),
    };
  }

  function createFallbackTrykkBox(option, sourceConfig) {
    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    if (!details.code) {
      return null;
    }

    const box = document.createElement("div");
    box.className = "trykk-box";
    box.dataset.trykkBox = "1";
    box.dataset.trykkCode = details.code;
    box.dataset.inputType = details.inputType;
    box.dataset.required = String(details.required);
    box.dataset.defaultOn = String(details.defaultSelected || details.required || details.locked);
    box.dataset.trykkShopifyVariantId = details.variantId;
    box.dataset.trykkVariantSku = details.variantSku;
    box.dataset.clubPageFallback = "true";

    if (typeof details.maxLength === "number") {
      box.dataset.maxLen = String(details.maxLength);
    }
    if (typeof details.rangeMin === "number") {
      box.dataset.min = String(details.rangeMin);
    }
    if (typeof details.rangeMax === "number") {
      box.dataset.max = String(details.rangeMax);
    }
    if (typeof details.defaultNumberValue === "number") {
      box.dataset.defaultNumberValue = String(details.defaultNumberValue);
    }
    if (details.numberUiMode) {
      box.dataset.numberUiMode = details.numberUiMode;
    }

    const head = document.createElement("div");
    head.className = "trykk-head";

    const left = document.createElement("div");
    left.className = "trykk-left";

    const title = document.createElement("div");
    title.className = "trykk-title";
    title.textContent = details.title;
    left.appendChild(title);

    if (details.priceText) {
      const price = document.createElement("div");
      price.className = "trykk-price";
      price.textContent = `+ ${details.priceText}`;
      left.appendChild(price);
    }

    const toggle = document.createElement("label");
    toggle.className = "trykk-toggle";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "js-trykk-enabled";

    const toggleText = document.createElement("span");
    toggleText.textContent = "Legg til";

    const hint = document.createElement("span");
    hint.className = "trykk-hint js-trykk-hint";

    toggle.appendChild(checkbox);
    toggle.appendChild(toggleText);
    toggle.appendChild(hint);

    head.appendChild(left);
    head.appendChild(toggle);

    const meta = document.createElement("div");
    meta.className = "trykk-meta";

    const metaLabel = document.createElement("div");
    metaLabel.className = "trykk-meta-label";
    metaLabel.textContent = "Antall trykk:";

    const metaValue = document.createElement("input");
    metaValue.className = "trykk-meta-input js-trykk-qty";
    metaValue.type = "text";
    metaValue.value = "0";
    metaValue.readOnly = true;

    meta.appendChild(metaLabel);
    meta.appendChild(metaValue);

    const extra = document.createElement("div");
    extra.className = "js-trykk-extra";
    extra.style.display = "none";

    const inputs = document.createElement("div");
    inputs.className = "js-trykk-inputs";
    extra.appendChild(inputs);

    const help = document.createElement("div");
    help.className = "trykk-help";
    help.textContent = buildFallbackHelpText(details.inputType);
    extra.appendChild(help);

    box.appendChild(head);
    box.appendChild(meta);
    box.appendChild(extra);

    return box;
  }

  function bindFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement) || box.dataset.clubPageFallbackBound === "true") {
      return;
    }

    box.dataset.clubPageFallbackBound = "true";
    const enabled = box.querySelector(".js-trykk-enabled");
    if (!(enabled instanceof HTMLInputElement)) {
      return;
    }

    const sync = () => syncFallbackTrykkBox(box, option, sourceConfig);

    enabled.addEventListener("change", sync);
    document.addEventListener("input", (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.matches('input[name="quantity"], .quantity__input')) {
        sync();
      }
    });
    document.addEventListener("change", (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.matches('input[name="quantity"], .quantity__input')) {
        sync();
      }
    });
    sync();
  }

  function updateFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement)) {
      return;
    }

    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    box.dataset.inputType = details.inputType;
    box.dataset.required = String(details.required);
    box.dataset.defaultOn = String(details.defaultSelected || details.required || details.locked);
    box.dataset.trykkShopifyVariantId = details.variantId;
    box.dataset.trykkVariantSku = details.variantSku;
    box.dataset.numberUiMode = details.numberUiMode;

    if (typeof details.maxLength === "number") {
      box.dataset.maxLen = String(details.maxLength);
    } else {
      delete box.dataset.maxLen;
    }
    if (typeof details.rangeMin === "number") {
      box.dataset.min = String(details.rangeMin);
    } else {
      delete box.dataset.min;
    }
    if (typeof details.rangeMax === "number") {
      box.dataset.max = String(details.rangeMax);
    } else {
      delete box.dataset.max;
    }
    if (typeof details.defaultNumberValue === "number") {
      box.dataset.defaultNumberValue = String(details.defaultNumberValue);
    } else {
      delete box.dataset.defaultNumberValue;
    }

    const title = box.querySelector(".trykk-title");
    if (title instanceof HTMLElement) {
      title.textContent = details.title;
    }

    const price = box.querySelector(".trykk-price");
    if (details.priceText) {
      if (price instanceof HTMLElement) {
        price.textContent = `+ ${details.priceText}`;
      } else {
        const left = box.querySelector(".trykk-left");
        if (left instanceof HTMLElement) {
          const priceNode = document.createElement("div");
          priceNode.className = "trykk-price";
          priceNode.textContent = `+ ${details.priceText}`;
          left.appendChild(priceNode);
        }
      }
    } else if (price instanceof HTMLElement) {
      price.remove();
    }

    const help = box.querySelector(".trykk-help");
    if (help instanceof HTMLElement) {
      help.textContent = buildFallbackHelpText(details.inputType);
    }

    bindFallbackTrykkBox(box, option, sourceConfig);
    syncFallbackTrykkBox(box, option, sourceConfig);
  }

  function syncFallbackTrykkBox(box, option, sourceConfig) {
    if (!(box instanceof HTMLElement) || box.dataset.clubPageFallback !== "true") {
      return;
    }

    const enabled = box.querySelector(".js-trykk-enabled");
    const extra = box.querySelector(".js-trykk-extra");
    const hint = box.querySelector(".js-trykk-hint");
    const quantityView = box.querySelector(".js-trykk-qty");
    const quantityInput = getQuantityInput();

    if (
      !(enabled instanceof HTMLInputElement) ||
      !(extra instanceof HTMLElement) ||
      !(quantityView instanceof HTMLInputElement)
    ) {
      return;
    }

    const quantity = getQuantityValue(quantityInput);
    quantityView.value = String(quantity);

    if (!enabled.checked) {
      extra.style.display = "none";
      if (hint instanceof HTMLElement) {
        hint.textContent = "";
      }
      clearFallbackTrykkInputs(box);
      return;
    }

    extra.style.display = "";
    if (hint instanceof HTMLElement) {
      hint.textContent = `(følger antall: ${quantity})`;
    }
    renderFallbackTrykkInputs(box, option, sourceConfig, quantity);
  }

  function renderFallbackTrykkInputs(box, option, sourceConfig, quantity) {
    const container = box.querySelector(".js-trykk-inputs");
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const details = resolveFallbackTrykkDetails(option, sourceConfig);
    if (details.inputType === "none" || quantity <= 0) {
      container.innerHTML = "";
      return;
    }

    const preserved = readFallbackTrykkValues(box, details.inputType);
    container.innerHTML = "";

    for (let index = 0; index < quantity; index += 1) {
      const row = document.createElement("div");
      row.className = "trykk-row";

      const counter = document.createElement("div");
      counter.className = "trykk-idx";
      counter.textContent = `${index + 1}.`;
      row.appendChild(counter);

      if (details.inputType === "text") {
        const input = document.createElement("input");
        input.type = "text";
        input.className = "js-trykk-unit";
        input.placeholder = "Navn";
        input.style.flex = "1";
        if (typeof details.maxLength === "number") {
          input.maxLength = details.maxLength;
        }
        input.value = String(preserved[index] || "");
        row.appendChild(input);
        container.appendChild(row);
        continue;
      }

      if (details.inputType === "number_range") {
        row.appendChild(buildFallbackNumberInput(details, preserved[index]));
        container.appendChild(row);
        continue;
      }

      if (details.inputType === "name_number") {
        const currentValue = preserved[index] && typeof preserved[index] === "object"
          ? preserved[index]
          : {};
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.className = "js-trykk-unit js-trykk-name";
        nameInput.placeholder = "Navn";
        nameInput.style.flex = "1";
        if (typeof details.maxLength === "number") {
          nameInput.maxLength = details.maxLength;
        }
        nameInput.value = String(currentValue.name || "");
        row.appendChild(nameInput);

        const numberInput = buildFallbackNumberInput(details, currentValue.number || "");
        numberInput.classList.add("js-trykk-number");
        if (numberInput instanceof HTMLInputElement) {
          numberInput.classList.add("js-trykk-unit");
        } else {
          numberInput.classList.add("js-trykk-unit");
        }
        row.appendChild(numberInput);
        container.appendChild(row);
      }
    }
  }

  function buildFallbackNumberInput(details, currentValue) {
    const min = typeof details.rangeMin === "number" ? Number(details.rangeMin) : 0;
    const max = typeof details.rangeMax === "number" ? Number(details.rangeMax) : 99;
    const safeMin = Math.min(min, max);
    const safeMax = Math.max(min, max);
    const defaultValue =
      currentValue !== undefined && currentValue !== null && String(currentValue).trim() !== ""
        ? String(currentValue)
        : typeof details.defaultNumberValue === "number"
          ? String(details.defaultNumberValue)
          : String(safeMin);

    if (details.numberUiMode === "select") {
      const select = document.createElement("select");
      select.style.width = "120px";
      for (let value = safeMin; value <= safeMax; value += 1) {
        const option = document.createElement("option");
        option.value = String(value);
        option.textContent = String(value);
        if (String(value) === defaultValue) {
          option.selected = true;
        }
        select.appendChild(option);
      }
      return select;
    }

    const input = document.createElement("input");
    input.type = "number";
    input.min = String(safeMin);
    input.max = String(safeMax);
    input.className = "js-trykk-unit";
    input.style.width = "120px";
    input.value = defaultValue;
    return input;
  }

  function readFallbackTrykkValues(box, inputType) {
    if (inputType === "text" || inputType === "number_range") {
      return Array.from(box.querySelectorAll(".js-trykk-unit")).map((input) =>
        String(input.value || ""),
      );
    }

    if (inputType === "name_number") {
      return Array.from(box.querySelectorAll(".trykk-row")).map((row) => {
        const nameInput = row.querySelector(".js-trykk-name");
        const numberInput = row.querySelector(".js-trykk-number");
        return {
          name: String(nameInput && "value" in nameInput ? nameInput.value || "" : ""),
          number: String(numberInput && "value" in numberInput ? numberInput.value || "" : ""),
        };
      });
    }

    return [];
  }

  function clearFallbackTrykkInputs(box) {
    const container = box.querySelector(".js-trykk-inputs");
    if (container instanceof HTMLElement) {
      container.innerHTML = "";
    }
  }

  function getQuantityInput() {
    return (
      document.querySelector('input.quantity__input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  function getQuantityValue(input) {
    const parsed = Number.parseInt(
      String(input && typeof input === "object" && "value" in input ? input.value : "1"),
      10,
    );
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 1;
    }

    return parsed;
  }

  function buildFallbackHelpText(inputType) {
    if (inputType === "text") {
      return "Skriv inn navn (en linje per trykk).";
    }
    if (inputType === "number_range") {
      return "Velg nummer (en linje per trykk).";
    }
    if (inputType === "name_number") {
      return "Skriv inn navn og velg nummer (en linje per trykk).";
    }
    return "Ingen input – valget legges til i handlekurven.";
  }

  function findOrCreateFallbackPack(mount, packTitle, code) {
    const packKey = normalizeToken(packTitle || code || "trykk");
    const existing = Array.from(mount.querySelectorAll("[data-trykk-pack]")).find((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return false;
      }

      return pack.dataset.clubPageFallbackPack === packKey;
    });

    if (existing instanceof HTMLElement) {
      return existing;
    }

    const pack = document.createElement("div");
    pack.className = "trykk-pack";
    pack.dataset.trykkPack = `club-page-${packKey}`;
    pack.dataset.clubPageFallbackPack = packKey;

    if (packTitle) {
      const title = document.createElement("div");
      title.className = "trykk-pack-title";
      title.textContent = packTitle;
      pack.appendChild(title);
    }

    mount.appendChild(pack);
    return pack;
  }

  function cleanupFallbackTrykkBoxes(activeCodes) {
    Array.from(document.querySelectorAll('[data-trykk-box][data-club-page-fallback="true"]')).forEach((box) => {
      if (!(box instanceof HTMLElement)) {
        return;
      }

      const code = normalizeToken(box.dataset.trykkCode || "");
      if (!activeCodes.has(code)) {
        removeFallbackTrykkBox(box);
      }
    });
  }

  function cleanupEmptyFallbackPacks() {
    Array.from(document.querySelectorAll("[data-club-page-fallback-pack]")).forEach((pack) => {
      if (!(pack instanceof HTMLElement)) {
        return;
      }

      const boxes = Array.from(pack.querySelectorAll("[data-trykk-box]")).filter((box) =>
        box instanceof HTMLElement,
      );
      if (boxes.length === 0) {
        pack.remove();
      }
    });
  }

  function removeFallbackTrykkBox(box) {
    if (!(box instanceof HTMLElement)) {
      return;
    }

    const pack = box.closest("[data-club-page-fallback-pack]");
    box.remove();
    if (pack instanceof HTMLElement) {
      const hasBoxes = pack.querySelector("[data-trykk-box]");
      if (!hasBoxes) {
        pack.remove();
      }
    }
  }

  function toOptionalNumber(value) {
    if (value === undefined || value === null || value === "") {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  function resolvePresentation(item) {
    const selectedValues = getSelectedColorCandidates();
    const secondaryTokens = buildColorTokens(item.secondaryColorRawValue, "");
    const useSecondary =
      Boolean(item.secondaryDisplayImageUrl) &&
      secondaryTokens.size > 0 &&
      selectedValues.some((value) => matchesColorValue(value, secondaryTokens));

    return {
      useSecondary,
      imageUrl: useSecondary && item.secondaryDisplayImageUrl
        ? item.secondaryDisplayImageUrl
        : item.displayImageUrl,
    };
  }

  function buildAllowedColorTokens(item) {
    const tokens = buildColorTokens(item.selectedColorRawValue, item.colorDisplay);
    const secondaryTokens = buildColorTokens(
      item.secondaryColorRawValue,
      item.secondaryColorRawValue,
    );

    secondaryTokens.forEach((token) => tokens.add(token));
    return tokens;
  }

  function getSelectedColorCandidates() {
    const values = [];
    const radioFields = Array.from(document.querySelectorAll("fieldset")).filter((fieldset) =>
      isColorField(fieldset),
    );

    radioFields.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!(checked instanceof HTMLInputElement)) {
        return;
      }

      const label =
        (checked.id && fieldset.querySelector(`label[for="${cssEscape(checked.id)}"]`)) ||
        checked.closest("label");
      const value = String(label?.textContent || checked.value || "").trim();
      if (value) {
        values.push(value);
      }
    });

    const selects = Array.from(document.querySelectorAll("select")).filter((select) =>
      isColorSelect(select),
    );

    selects.forEach((select) => {
      const option = select.selectedOptions && select.selectedOptions[0];
      const value = String((option && option.textContent) || select.value || "").trim();
      if (value) {
        values.push(value);
      }
    });

    return values;
  }

  function buildColorTokens(rawValue, displayValue) {
    const tokens = new Set();

    for (const value of [rawValue, displayValue]) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        continue;
      }

      tokens.add(normalizeToken(normalized));

      const dashMatch = normalized.match(/^(\d{2,6})[-/ ]+(.+)$/);
      if (dashMatch) {
        tokens.add(normalizeToken(dashMatch[1]));
        tokens.add(normalizeToken(dashMatch[2]));
      }

      const colonMatch = normalized.match(/^([^:]+):\s*(.+)$/);
      if (colonMatch) {
        tokens.add(normalizeToken(colonMatch[1]));
        tokens.add(normalizeToken(colonMatch[2]));
      }
    }

    return tokens;
  }

  function matchesColorValue(value, tokens) {
    const normalized = normalizeToken(value);
    if (!normalized) {
      return false;
    }

    if (tokens.has(normalized)) {
      return true;
    }

    return Array.from(tokens).some((token) => normalized.includes(token) || token.includes(normalized));
  }

  function isColorField(fieldset) {
    const label = [
      fieldset.getAttribute("aria-label"),
      fieldset.querySelector("legend")?.textContent,
      fieldset.dataset.optionName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return label.includes("farge") || label.includes("color") || label.includes("colour");
  }

  function isColorSelect(select) {
    const id = select.getAttribute("id") || "";
    const name = select.getAttribute("name") || "";
    const label =
      (id ? document.querySelector(`label[for="${cssEscape(id)}"]`)?.textContent : "") || "";
    const combined = `${id} ${name} ${label}`.toLowerCase();
    return combined.includes("farge") || combined.includes("color") || combined.includes("colour");
  }

  function normalizeBaseUrl(value) {
    if (!value) {
      return "";
    }

    return String(value).endsWith("/") ? String(value) : `${value}/`;
  }

  function normalizeToken(value) {
    return String(value || "")
      .normalize("NFKD")
      .replace(/[^\w\s/-]+/g, "")
      .trim()
      .toLowerCase();
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }
})();
